import { useEffect, useRef } from 'react';
import { useLocalization } from '@canvas-logic/clp-shared-ui';
import { isLanguageUnavailableError } from '@canvas-logic/clp-localization';

import type { LanguageIssue } from './languageFallback';

export interface LanguageDebugContext {
  projectId: number | undefined;
  catalogId: number | undefined;
  productId: number | undefined;
  draft: boolean | undefined;
}

interface Props {
  requestedLanguage: string | undefined;
  debugContext: LanguageDebugContext;
  onLanguageUnavailable: (issue: LanguageIssue) => void;
}

/**
 * Reports an unavailable `?lang=` to the owner of the `language` prop, which
 * is the only place that can re-issue the request. Discriminates on the
 * platform code, not the status — a malformed include/layers list is also a
 * 400 and must stay a visible bug.
 */
export function LanguageFallbackGuard({ requestedLanguage, debugContext, onLanguageUnavailable }: Props): null {
  const { status, error, resolvedLanguage } = useLocalization();
  // Keyed by language: a second bad ?lang= is worth reporting, a re-render isn't.
  const reportedFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'error' || !isLanguageUnavailableError(error) || requestedLanguage == null) return;
    if (reportedFor.current === requestedLanguage) return;
    reportedFor.current = requestedLanguage;

    // Nothing resolved yet = initial load, so there is no working language to keep.
    const fallbackApplied = resolvedLanguage == null;

    console.error(
      `[localization] Requested language "${requestedLanguage}" is not available; ` +
        (fallbackApplied ? 'falling back to the project default.' : `keeping "${resolvedLanguage}".`),
      {
        requestedLanguage,
        resolvedLanguage,
        fallbackApplied,
        appErrorCode: error.appErrorCode,
        status: error.status,
        serverMessage: error.message,
        projectId: debugContext.projectId,
        catalogId: debugContext.catalogId,
        productId: debugContext.productId,
        draft: debugContext.draft,
        // `live` → add the language to the project; `active` → re-release the catalog.
        route: debugContext.draft || debugContext.catalogId == null ? 'live' : 'active',
      },
    );

    onLanguageUnavailable({ requested: requestedLanguage, fallbackApplied });
  }, [status, error, resolvedLanguage, requestedLanguage, debugContext, onLanguageUnavailable]);

  return null;
}
